import {
  renderDescriptionParagraphs,
  RootState,
  updateSecureMessageRequest,
  useProductName,
} from '@rb-serve-app/core';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../components/Form';
import SuccessModel from '../../components/SuccessModel';
import { useRecaptchaToken } from '@rb-serve-app/core';

type FormData = {
  category: string;
  reason: string;
  comment: string;
};

const CreateMessage = ({ closeModal, fetchMessages }: any) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
    trigger,
  } = useForm<FormData>({ mode: 'onChange' });

  const dispatch = useDispatch();
  const category = watch('category');
  const reason = watch('reason');
  const comment = watch('comment');
  const [reasons, setReasons] = useState<string[]>([]);
  const [containsUrl, setContainsUrl] = useState(false);
  const getRecaptchaToken = useRecaptchaToken();

  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData || {};
  const { rb_secure_message, rb_change_password } = entries || {};
  const { secureMessageListInfo } = rb_secure_message || {};

  const { accountId, customerId, accountDetails } = useSelector(
    (state: RootState) => state.user
  );
  const {
    secureMessageOptions,
    updateSecureMessage,
    updateSecureMessageError,
    secureMessageOptionsLoading,
  } = useSelector((state: RootState) => state.contactInfo);

  // Organize categories and reasons from secureMessageOptions
  const [availableCategories, setAvailableCategories] = useState<
    { key: string; value: string }[]
  >([]);
  const [availableReasons, setAvailableReasons] = useState<
    { key: string; value: string }[]
  >([]);

  // Effect to organize categories and reasons from contentful data
  useEffect(() => {
    if (secureMessageListInfo?.categoryListForWeb) {
      // Extract categoryListForWeb from secureMessageListInfo
      const categories = secureMessageListInfo?.categoryListForWeb;

      setAvailableCategories(categories?.length > 0 ? categories : CATEGORIES);
    }
  }, [secureMessageListInfo]);

  useEffect(() => {
    if (category) {
      const categoryReason = Object.entries(secureMessageListInfo)
        .filter(([item]) => item.includes('FOR_WEB'))
        .filter(([item, value]) => {
          if (
            item.split('_').slice(0, -2).join(' ') === category.toUpperCase()
          ) {
            return value;
          }
        });
      setAvailableReasons(
        categoryReason[0]?.[1] as { key: string; value: string }[]
      );
    } else {
      // Fallback to default reasons if no contentful data
      setAvailableReasons(REASONS);
    }
  }, [category]);

  const checkForUrls = (text: string): boolean => {
    const urlRegex =
      /(?:https?:\/\/)?(?:www\.)?[-a-z0-9@:%._+~#=]{1,256}\.[a-z]{2,6}\b(?:[-a-z0-9()@:%_+.~#?&/=]*)/gi;
    return urlRegex.test(text);
  };
  const containsScriptTag = (text: string): boolean => {
    const scriptTagRegex = /<\s*script.*?>[\s\S]*?<\s*\/\s*script>/gi;
    return scriptTagRegex.test(text);
  };
  const containsHTMLTags = (text: string): boolean => {
    const htmlTagRegex = /<[^>]*>/g;
    return htmlTagRegex.test(text);
  };

  const onSubmit = async (data: FormData) => {
    console.log('Form Submitted:', data);
    // Clear any previous error banner

    // Dispatch action to submit secure message
    const token = await getRecaptchaToken('send_secure_message');
    dispatch(
      updateSecureMessageRequest({
        accountId,
        customerId,
        requestBody: {
          assignmentId: secureMessageOptions?.assignmentId || '',
          category,
          comments: comment,
          reason,
          recaptchaToken: token,
        },
      })
    );
  };
  const handelClose = () => {
    reset();
    fetchMessages();
  };
  const { cardStructure, productName } = useProductName();

  return (
    <div>
      <h2
        aria-label={rb_secure_message?.secureMessageInfo?.newMessageTitle}
        id="create-new-message"
        className="text-[16px] text-left md:text-[28px] font-[500] md:text-center mb-[2px] md:mb-4 text-[#1F1F1F]"
      >
        {rb_secure_message?.secureMessageInfo?.newMessageTitle || 'New message'}
      </h2>
      <p
        aria-label={` ${rb_secure_message?.secureMessageInfo?.newMessageSubTitle1} ${productName} ${accountDetails?.customer?.cardLast4} ${rb_secure_message?.secureMessageInfo?.newMessageSubTitle2}`}
        className="text-black text-left md:text-center text-[15px] font-[400] mb-4 leading-[20px]"
      >
        {rb_secure_message?.secureMessageInfo?.newMessageSubTitle1 ||
          'Submit questions or comments about your Rogers'}
          {renderDescriptionParagraphs(rb_secure_message?.secureMessageInfo?.newMessageSubTitle1 + "  " + productName + " " + accountDetails?.customer?.cardLast4 + " " + rb_secure_message?.secureMessageInfo?.newMessageSubTitle2)}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Category */}
        <div>
          <label
            htmlFor="category"
            className="block text-[16px] font-[400] text-black"
            aria-label={rb_secure_message?.categoryTitle}
          >
            {rb_secure_message?.categoryTitle}
            <span className="text-primary-red">*</span>
          </label>
          <select
            id="category"
            {...register('category', { required: 'Category is required' })}
            className="mt-1 w-full border rounded px-3 py-2"
          >
            <option value="">{secureMessageListInfo?.selectCategory}</option>
            {availableCategories?.map((cat) => (
              <option key={cat.key} value={cat.value}>
                {cat.key}
              </option>
            ))}
          </select>
          {errors.category && (
            <p
              className="text-red-600 text-sm mt-1"
              role="alert"
              aria-label={errors.category.message}
            >
              {errors.category.message}
            </p>
          )}
        </div>

        {/* Reason */}
        <div>
          <label
            htmlFor="reason"
            className="block text-[16px] font-[400] text-black"
            aria-label={rb_secure_message?.reasonTitle}
          >
            {rb_secure_message?.reasonTitle}
            <span className="text-primary-red">*</span>
          </label>
          <select
            id="reason"
            {...register('reason', { required: 'Reason is required' })}
            className="mt-1 w-full border rounded px-3 py-2"
          >
            <option value="" aria-label={secureMessageListInfo?.selectCategory}>
              {secureMessageListInfo?.selectCategory}
            </option>
            {category &&
              availableReasons?.map((reason) => (
                <option
                  key={reason.key}
                  value={reason.value}
                  aria-label={reason.key}
                >
                  {reason.key}
                </option>
              ))}
          </select>
          {errors.reason && (
            <p
              className="text-red-600 text-sm mt-1"
              role="alert"
              aria-label={errors.reason.message}
            >
              {errors.reason.message}
            </p>
          )}
        </div>

        {/* Comment */}
        <div>
          <label
            htmlFor={rb_secure_message?.commentTitle}
            className="block text-[16px] font-[400] text-black"
            aria-label={rb_secure_message?.secureMessageListInfo?.addComment}
          >
            {rb_secure_message?.secureMessageListInfo?.addComment ||
              'Add comment'}
            <span className="text-primary-red">*</span>
          </label>
          <textarea
            id={rb_secure_message?.commentTitle}
            rows={6}
            {...register('comment', {
              required:
                rb_secure_message?.errorMessageInfo?.commentRequiredError,
              minLength: {
                value: 5,
                message: rb_secure_message?.errorMessageInfo?.minLengthError,
              },
              maxLength: {
                value: 500,
                message: rb_secure_message?.errorMessageInfo?.maxLengthError,
              },
              validate: (value) => {
                if (checkForUrls(value)) {
                  return rb_secure_message?.errorMessageInfo?.checkForURLError;
                }
                if (containsScriptTag(value) || containsHTMLTags(value)) {
                  return rb_secure_message?.errorMessageInfo
                    ?.chekForHtmlScriptError;
                }
                return true;
              },
            })}
            placeholder={
              rb_secure_message?.secureMessageListInfo?.typeCommentHere
            }
            className="mt-1 w-full border rounded px-3 py-2"
          />
          {errors.comment && (
            <p
              className="text-red-600 text-sm mt-1"
              role="alert"
              aria-label={errors.comment.message}
            >
              {errors.comment.message}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex flex-1 justify-center items-center gap-2">
          <button
            aria-label={rb_secure_message?.errorMessageInfo?.cancelCta}
            type="button"
            className="px-4 py-2 border border-primary-red text-primary-red  hover:bg-gray-100 w-full h-[48px]"
            onClick={() => closeModal()}
          >
            {rb_secure_message?.errorMessageInfo?.cancelCta}
          </button>
          <div className="relative w-full">
            <Button
              text={
                secureMessageOptionsLoading
                  ? ''
                  : rb_secure_message?.errorMessageInfo?.sbmitCta
              }
              disabled={!isValid}
              loader={secureMessageOptionsLoading}
              aria-busy={secureMessageOptionsLoading}
            />
            {secureMessageOptionsLoading && (
              <div className="flex justify-center items-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6"></div>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

// Fallback category and reason options in case API fails
export const CATEGORIES = [
  {
    key: 'Profile update',
    value: 'Profile update',
  },
  {
    key: 'General question',
    value: 'General question',
  },
  {
    key: 'Report a system issue',
    value: 'Report a system issue',
  },
  {
    key: 'Account specific question',
    value: 'Account specific question',
  },
];

export const REASONS = [
  {
    key: 'Other',
    value: 'Other',
  },
  {
    key: 'Name change',
    value: 'Name change',
  },
  {
    key: 'Language change',
    value: 'Language change',
  },
  {
    key: 'Pre-Authorized Debit',
    value: 'Pre-Authorized Debit',
  },
  {
    key: 'Payment due date change',
    value: 'Payment due date change',
  },
];

export default CreateMessage;
